import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateTaskCustom_GetAllEstateTaskCustomsByPortalIdDocument,
  EstateTaskCustom_GetAllEstateTaskCustomsByPortalIdQuery,
  EstateTaskGroup,
  EstateTaskInstance,
  EstateTaskInstance_GetAllEstateTaskInstancesByEstateIdDocument,
  EstateTaskInstance_GetAllEstateTaskInstancesByEstateIdQuery,
  EstateType,
  useEstateTaskCustom_CreateEstateTaskCustomMutation,
  useEstateTaskCustom_GetAllEstateTaskCustomsByPortalIdQuery,
  useEstateTaskInstance_GetAllEstateTaskInstancesByEstateIdQuery,
} from 'queries';
import * as yup from 'yup';

import FormTextField from 'common/form-text-field';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

type Props = {
  estateTaskGroup: EstateTaskGroup;
  estateId: string;
  estateType: EstateType;
  groupedEstateTaskInstances: { [key: string]: EstateTaskInstance[] };
  // eslint-disable-next-line @typescript-eslint/ban-types
  addGroupedEstateTaskInstance: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onExitClick: Function;
};

const useStyles = makeStyles((theme) => ({
  form: {
    textAlign: 'left',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
    color: 'red',
  },
  modalContentWrapper: {
    marginTop: '18px',
    marginBottom: '8px',
    padding: '18px',
    paddingTop: '18px',
    '& input': {
      background: 'white',
    },
  },
  modalButtonWrapper: {
    marginBottom: '18px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '4px',
  },
  headerWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '4px',
  },
  headerItemWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0px',
    gap: '13.54px',
    fontSize: '24px',
    lineHeight: '30px',
    marginBottom: '8px',
  },
  headerLogo: {
    width: '32.5px',
    height: '32.5px',
  },
  textItemWrapper: {
    textAlign: 'center',
  },
  detailsLineWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'left',
    alignItems: 'left',
    textAlign: 'left',
    flexGrow: 1,
  },
  detailsHeaderWrapper: {
    fontSize: '18px',
    lineHeight: '23px',
    textAlign: 'left',
    marginTop: '18px',
    marginBottom: '18px',
    fontWeight: 600,
  },
  detailsTitleWrapper: {
    fontSize: '14px',
    lineHeight: '18px',
    textAlign: 'left',
    fontWeight: 600,
  },
  detailsInputField: {
    paddingTop: '10px',
    paddingBotton: '10px',
    marginBottom: '18px',
  },
  detailsSelectOption: {
    cursor: 'pointer',
  },
  cancelButton: {
    marginRight: theme.spacing(2),
    backgroundColor: '#e0e0e0',
  },
  customizeHeader: {
    textAlign: 'center',
    backgroundColor: '#DAE4ED',
    padding: '5px',
    marginTop: '20px',
    marginBottom: '20px',
  },
  label: {
    textAlign: 'center',
    border: '1px solid #CCCCCC',
    padding: '8px 12px',
    gap: '10px',
    flex: 'none',
    marginBottom: '18px',
  },
}));

// set up required fields and validation
const requiredTextFields = ['label'];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const requiredTextFieldValidation = requiredTextFields.reduce((acc: any, val: string) => {
  acc[val] = yup.string().required('Required');
  return acc;
}, {});
const schema = yup.object().shape({
  ...requiredTextFieldValidation,
});

type EstateTaskCustomFormValues = {
  label: string;
  labelHint: string;
  sortOrder: number;
  createForPreviousEstates: boolean;
};

export default function EstateTaskCustomForm({
  estateTaskGroup,
  estateId,
  estateType,
  groupedEstateTaskInstances,
  addGroupedEstateTaskInstance,
  onExitClick,
}: Props) {
  const classes = useStyles();

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    trigger,
  } = useForm<EstateTaskCustomFormValues>({
    // @ts-expect-error - RHF V7 limitation #7895
    resolver: yupResolver(schema),
    defaultValues: {
      createForPreviousEstates: false,
    },
  });

  const label = watch('label');
  const [showConfirmNewTask, setShowConfirmNewTask] = useState(false);

  const { portalId } = useParams<Record<string, string>>();

  // Load all custom estate tasks into the cache.
  const { data: estateTaskCustomsData, loading: estateTaskCustomsLoading } =
    useEstateTaskCustom_GetAllEstateTaskCustomsByPortalIdQuery({
      variables: {
        input: {
          portalId,
        },
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting all custom estate tasks: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  // Load all estate task instances into the cache.
  const { data: estateTaskInstancesData, loading: estateTaskInstancesLoading } =
    useEstateTaskInstance_GetAllEstateTaskInstancesByEstateIdQuery({
      variables: {
        input: {
          estateId,
          portalId,
        },
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting all estate task instances: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [createEstateTaskCustom, { loading: createEstateTaskCustomExecuting }] =
    useEstateTaskCustom_CreateEstateTaskCustomMutation({
      onCompleted: () => {
        showToast({
          message: 'Successfully created new custom estate task!',
          severity: NotificationSeverity.SUCCESS,
        });
        onExitClick();
      },
      update: (store, { data: newEstateTaskCustomData }) => {
        const newEstateTaskCustom = newEstateTaskCustomData?.data;
        if (newEstateTaskCustom) {
          addGroupedEstateTaskInstance(
            newEstateTaskCustom.estateTaskInstance!.group!,
            newEstateTaskCustom.estateTaskInstance!,
          );

          const updatedEstateTaskCustomsData =
            store.readQuery<EstateTaskCustom_GetAllEstateTaskCustomsByPortalIdQuery>({
              query: EstateTaskCustom_GetAllEstateTaskCustomsByPortalIdDocument,
              variables: { input: { portalId } },
            });

          const updatedEstateTaskInstancesData =
            store.readQuery<EstateTaskInstance_GetAllEstateTaskInstancesByEstateIdQuery>({
              query: EstateTaskInstance_GetAllEstateTaskInstancesByEstateIdDocument,
              variables: { input: { estateId, portalId } },
            });

          if (updatedEstateTaskCustomsData && updatedEstateTaskInstancesData) {
            const updatedEstateTaskCustoms = updatedEstateTaskCustomsData
              ? [...updatedEstateTaskCustomsData.data, newEstateTaskCustom.estateTaskCustom!]
              : [newEstateTaskCustom.estateTaskCustom!];
            store.writeQuery<EstateTaskCustom_GetAllEstateTaskCustomsByPortalIdQuery>({
              query: EstateTaskCustom_GetAllEstateTaskCustomsByPortalIdDocument,
              data: {
                data: updatedEstateTaskCustoms,
              },
              variables: { input: { portalId } },
            });

            const updatedEstateTaskInstances = updatedEstateTaskInstancesData
              ? [...updatedEstateTaskInstancesData.data, newEstateTaskCustom.estateTaskInstance!]
              : [newEstateTaskCustom.estateTaskInstance!];

            store.writeQuery<EstateTaskInstance_GetAllEstateTaskInstancesByEstateIdQuery>({
              query: EstateTaskInstance_GetAllEstateTaskInstancesByEstateIdDocument,
              data: {
                data: updatedEstateTaskInstances as EstateTaskInstance[],
              },
              variables: { input: { estateId, portalId } },
            });
          }
        }
      },
      onError: (e) => {
        showAlert({
          message: 'Error creating new custom estate task: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  if (!estateTaskCustomsData && estateTaskCustomsLoading) {
    return <Spinner />;
  }

  if (!estateTaskInstancesData && estateTaskInstancesLoading) {
    return <Spinner />;
  }

  const getNewSortOrderForTaskGroup = (group: EstateTaskGroup) => {
    const taskGroup = groupedEstateTaskInstances[group];
    const firstTaskSortOrder = taskGroup.length ? taskGroup[0].sortOrder! : 1;
    const lastTaskSortOrder = taskGroup[taskGroup.length - 1].sortOrder ?? firstTaskSortOrder;
    return lastTaskSortOrder + 1;
  };

  const submitForm = (formValues: EstateTaskCustomFormValues) => {
    const sortOrder = formValues.sortOrder || getNewSortOrderForTaskGroup(estateTaskGroup);

    createEstateTaskCustom({
      variables: {
        input: {
          portalId,
          estateId,
          estateType,
          group: estateTaskGroup,
          label: formValues.label,
          labelHint: formValues.labelHint,
          sortOrder: parseInt(sortOrder.toString()), // needed to avoid GraphQL error: Float cannot represent non numeric value
          createForPreviousEstates: formValues.createForPreviousEstates,
        },
      },
    });
    setShowConfirmNewTask(false);
  };

  const handleNextClick = async () => {
    const result = await trigger();

    if (result) {
      setShowConfirmNewTask(true);
    }
  };

  return (
    <>
      <form
        id="estate-task-custom-form"
        name="estate-task-custom-form"
        className={classes.form}
        onSubmit={handleSubmit(submitForm)}
      >
        {!showConfirmNewTask ? (
          <>
            <div className={classes.modalContentWrapper}>
              <div className={classes.headerWrapper}>
                <div className={classes.headerItemWrapper}>
                  <img
                    className={classes.headerLogo}
                    src="/trustate-logo.svg"
                    alt="trustate-logo"
                  />
                </div>
                <div className={classes.headerItemWrapper}>
                  <span>Add a new task</span>
                </div>
              </div>
              <div className={classes.detailsLineWrapper}>
                <Grid container>
                  <Typography className={classes.detailsHeaderWrapper}>
                    Enter the details for your new task
                  </Typography>
                </Grid>
              </div>
              <div className={classes.detailsLineWrapper}>
                <Grid container>
                  <Controller
                    name="label"
                    control={control}
                    render={({ field: { ref, value, ...field } }) => (
                      <FormTextField
                        {...field}
                        inputRef={ref}
                        value={value || ''}
                        autoFocus
                        errorMessage={errors.label?.message}
                        label="Task name"
                      />
                    )}
                  />
                </Grid>
              </div>

              <div className={classes.detailsLineWrapper}>
                <Grid container>
                  <Controller
                    name="labelHint"
                    control={control}
                    render={({ field: { ref, value, ...field } }) => (
                      <FormTextField
                        {...field}
                        inputRef={ref}
                        value={value || ''}
                        autoFocus
                        label="Description (optional)"
                        errorMessage={errors.labelHint?.message}
                      />
                    )}
                  />
                </Grid>
              </div>

              <div className={classes.detailsLineWrapper}>
                <Grid container>
                  <Controller
                    name="sortOrder"
                    control={control}
                    render={({ field: { ref, value, ...field } }) => (
                      <FormTextField
                        {...field}
                        inputRef={ref}
                        type="number"
                        value={value || undefined}
                        label="Sort Order"
                        errorMessage={errors.sortOrder?.message}
                        InputProps={{
                          inputProps: {
                            min: 0,
                            max: 9999,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </div>

              <div className={classes.detailsLineWrapper}>
                <Grid container>
                  <Controller
                    name="createForPreviousEstates"
                    control={control}
                    render={({ field: { ref, onChange } }) => (
                      <FormControlLabel
                        className={classes.detailsInputField}
                        label="Apply this task to all current and future dashboards of this type in my firm."
                        labelPlacement="start"
                        control={
                          <Checkbox
                            inputRef={ref}
                            name="createForPreviousEstates"
                            color="primary"
                            onChange={(evt) => onChange(evt.target.checked)}
                          />
                        }
                      />
                    )}
                  />
                </Grid>
              </div>
            </div>
            <div className={classes.modalButtonWrapper}>
              <Button
                type="button"
                className={classes.cancelButton}
                onClick={() => onExitClick()}
                variant="contained"
              >
                Cancel
              </Button>
              <Button
                type="button"
                color="primary"
                form="estate-task-custom-form"
                variant="contained"
                onClick={handleNextClick}
              >
                Next
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className={classes.modalContentWrapper}>
              <div className={classes.headerWrapper}>
                <div className={classes.headerItemWrapper}>
                  <img
                    className={classes.headerLogo}
                    src="/trustate-logo.svg"
                    alt="trustate-logo"
                  />
                </div>
                <div className={classes.headerItemWrapper}>
                  <span>Add the following task to your list?</span>
                </div>
              </div>
              <div className={classes.label}>{label}</div>
              <div className={classes.textItemWrapper}>
                <span>This will be added onto your estate Task List.</span>
              </div>
            </div>
            <div className={classes.modalButtonWrapper}>
              <Button
                className={classes.cancelButton}
                onClick={() => onExitClick()}
                variant="contained"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                form="estate-task-custom-form"
                variant="contained"
                disabled={isSubmitting || createEstateTaskCustomExecuting}
                type="submit"
              >
                Confirm
              </Button>
            </div>
          </>
        )}
      </form>
    </>
  );
}
